import { SaveOutlined } from '@ant-design/icons';
import { Card, Checkbox, Col, Divider, Form, Input, InputNumber, message, Modal, Row, Select, Spin } from 'antd';
import React, { useState } from 'react';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { ModalWidth, ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import CountryInput from '@/components/FormItems/CountryInput';
import { BlobStorageContainerType, EmployeeType, TransportRoleType } from '@/constants';
import CreateBtn from '@/components/app/CreateBtn';
import MainContent from '@/layouts/MainContent';
import { Helmet } from 'react-helmet';
import RegionInput from '@/components/FormItems/RegionInput';
import CityInput from '@/components/FormItems/CityInput';
import SingleImageUpload from '@/components/FormItems/SingleImageUpload';
import { getFullName } from '@/helpers/string';
import BooleanTag from '@/components/app/BooleanTag';
import AppDatePicker from '@/components/FormItems/DatePicker';
import EmployeeCategoryInput from '@/components/FormItems/EmployeeCategoryInput';
import ExportColumnsSelectorModal from '@/components/modals/ExportColumnsSelectorModal';
import { exportService } from '@/services/exportService';
import ExportBtn from '@/components/app/ExportBtn';
import { clearAllCompanyUsersCache } from '@/hooks/data/masterDataHook';
import { CompanyUserDto, CompanyUserStationLinkModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
import PhoneCodeInput from '@/components/FormItems/PhoneCodeInput';
import StationInput from '@/components/FormItems/StationInput';
const PAGE_TITLE = 'staffsPage_Title';
const CHECK_IN_OPERATOR = 'CheckIn Operator';

const columns: IColumnType<CompanyUserDto>[] = [
  {
    title: 'base.FullName',
    dataIndex: 'name',
    width: 200,
    ellipsis: true,
    render: (_, record) => getFullName(record.firstName, record.lastName),
  },
  {
    title: 'staffsPage_EmployeeId',
    dataIndex: 'employeeUniqueId',
    width: 100,
    ellipsis: true,
  },
  {
    title: 'staffsPage_EmployeeCode',
    dataIndex: 'employeeCode',
    width: 100,
    ellipsis: true,
  },
  {
    title: 'base.Email',
    dataIndex: 'email',
    width: 150,
    ellipsis: true,
  },
  {
    title: 'base.PhoneNumber',
    dataIndex: 'phoneNumber',
    width: 100,
    ellipsis: true,
    render: (v, { phoneCode }: any) => [phoneCode, v].filter(x => x).join('-'),
  },
  {
    title: 'staffsPage_EmployeeCategory',
    dataIndex: 'employeeCategory',
    ellipsis: true,
    width: 150,
  },
  {
    title: 'staffsPage_JobTitle',
    dataIndex: 'jobTitle',
    ellipsis: true,
    width: 130,
  },
  {
    title: 'staffsPage_CanLogin',
    dataIndex: 'canLogin',
    width: 100,
    render: v => <BooleanTag value={v} />,
    align: 'center',
  },
];

const filters: Array<IFilterItem> = [
  { type: 'TEXT', name: 'searchText', label: 'staffsPage_SearchText' },
  { type: 'TEXT', name: 'UserName', label: 'base.UserName' },
  { type: 'LIST', name: 'employeeType', label: 'staffsPage_EmployeeType', listData: EmployeeType.__getSelectOptions() },
];

const AccountTab = () => {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showExportModal, toggleExportModal] = React.useState<boolean>(false);
  const [exportColumns, setExportColumns] = React.useState<string[]>([]);

  const [roles, setRoles] = React.useState<Array<{ value: any; label: any }>>([]);
  const [permissions, setPermissions] = React.useState<Array<{ value: any; label: any }>>([]);
  const [otherLinks, setOtherLinks] = React.useState<CompanyUserStationLinkModel[]>([]);
  const selectedRoles: string[] = Form.useWatch('roles', form) || [];

  React.useEffect(() => {
    apiSdk.RoleService.getCompanyRoleNames().then(res => {
      setRoles(res.map(x => ({ value: x, label: x })));
    });
    apiSdk.RoleService.getCompanyPermissions().then(res => {
      setPermissions(res?.map(x => ({ value: x, label: x })) || []);
    });
  }, []);

  const clearCache = () => {
    clearAllCompanyUsersCache();
  };

  React.useEffect(() => {
    apiSdk.CompanyUserService.getExportColumns().then(res => {
      setExportColumns(res);
    });
  }, []);

  const createEntity = values => {
    setLoading(true);
    apiSdk.CompanyUserService.create({ body: { ...values } })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          clearCache();
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateEntity = values => {
    setLoading(true);
    apiSdk.CompanyUserService.update({ body: { ...values } })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          clearCache();
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const onEdit = (id: string) => {
    setLoading(true);
    apiSdk.CompanyUserService.getDetails({ id: id })
      .then(res => {
        if (res) {
          setIsNewForm(false);
          setFormVisible(true);
          setOtherLinks(res.otherStationLinks || []);
          form.setFieldsValue({
            ...res,
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onExportExcel = (cols: string[]) => {
    exportService
      .exportStaffs({
        ...(zTb.current?.getFilter() || {}),
        columns: cols,
      })
      .toExcel();
  };

  const onExportPdf = (cols: string[]) => {
    exportService
      .exportStaffs({
        ...(zTb.current?.getFilter() || {}),
        columns: cols,
      })
      .toPdf();
  };
  const isCashPaymentSelected = Form.useWatch('isCashPaymentSelected', form);
  const isMobilePaymentSelected = Form.useWatch('isMobilePaymentSelected', form);
  const isBankPaymentSelected = Form.useWatch('isBankPaymentSelected', form);
  const employeeType = Form.useWatch('employeeType', form);
  const countryId = Form.useWatch('countryId', form);
  const regionId = Form.useWatch('regionId', form);
  return (
    <MainContent
      title={t(PAGE_TITLE)}
      isLoading={loading}
      extraBtns={
        <CreateBtn
          onClick={() => {
            setIsNewForm(true);
            setFormVisible(true);
            form.resetFields();
          }}
        />
      }
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable<CompanyUserDto>
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          defaultVariables={{
            employeeType: EmployeeType.Employee,
          }}
          url="/user-accounts/company-users/get-company-users"
          bordered
          order_by="createdOn"
          order_by_asc={true}
          allowEdit={true}
          onEdit={record => {
            onEdit(record.id || '');
          }}
          allowDelete={true}
          delete_url="/user-accounts/company-users/delete"
          filters={filters}
          searchBtnAlignLeft
          onDeleteCompleted={clearCache}
          extraBtns={
            <ExportBtn
              disabled={!exportColumns?.length}
              onClick={() => {
                toggleExportModal(true);
              }}
            />
          }
        />
        {formVisible ? (
          <Modal
            open={formVisible}
            maskClosable={false}
            width={ModalWidth.extraLarge}
            className="nta-custom-form"
            title={isNewForm ? t('staffsPage_AddStaff') : t('staffsPage_UpdateStaff')}
            forceRender={true}
            style={{ top: 10 }}
            okText={t('save')}
            cancelText={t('cancel')}
            okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
            onCancel={() => {
              form.resetFields();
              setFormVisible(false);
            }}
            onOk={() => {
              form
                .validateFields()
                .then(values => {
                  if (values.id) {
                    updateEntity(values);
                  } else {
                    createEntity(values);
                  }
                })
                .catch(info => {});
            }}
          >
            <Spin spinning={loading}>
              <Form
                form={form}
                size="middle"
                layout="horizontal"
                initialValues={{ employeeType: EmployeeType.Employee }}
                labelAlign="right"
                labelCol={{ md: 10 }}
              >
                <Form.Item name="id" className="hidden">
                  <Input type="hidden" />
                </Form.Item>
                <Row gutter={ROW_GUTTER}>
                  <Col md={8}>
                    <Form.Item label={t('base.FirstName')} name="firstName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input />
                    </Form.Item>

                    <Form.Item label={t('base.LastName')} name="lastName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input />
                    </Form.Item>

                    <Form.Item label={t('staffsPage_EmployeeId')} name="employeeUniqueId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item label={t('staffsPage_EmployeeCode')} name="employeeCode" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item label={t('base.Email')} name="email" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input type="email" disabled={!isNewForm} className="input-value-color" />
                    </Form.Item>

                    <Form.Item label={t('base.Country')} name="countryId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <CountryInput
                        onChange={() => {
                          form.setFieldValue('regionId', null);
                        }}
                      />
                    </Form.Item>
                    <Form.Item label={t('base.Region')} name="regionId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <RegionInput
                        onChange={() => {
                          form.setFieldValue('cityId', null);
                        }}
                        countryId={countryId}
                        disabled={!countryId}
                      />
                    </Form.Item>
                    <Form.Item label={t('base.City')} name="cityId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <CityInput regionId={regionId} disabled={!regionId} />
                    </Form.Item>
                    <Form.Item label={t('base.PhoneCode')} name="phoneCode" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <PhoneCodeInput />
                    </Form.Item>
                    <Form.Item label={t('base.PhoneNumber')} name="phoneNumber" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <Input />
                    </Form.Item>
                    <Form.Item label={t('staffsPage_JoiningDate')} name="employeeStartDate" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                      <AppDatePicker className="w-100" />
                    </Form.Item>
                    <Form.Item label={t('staffsPage_EmployeeCategory')} name="employeeCategoryId">
                      <EmployeeCategoryInput />
                    </Form.Item>
                    <Form.Item label={t('staffsPage_JobTitle')} name="jobTitle">
                      <Input />
                    </Form.Item>

                    <Form.Item label={t('staffsPage_IgrParts')} name="igrParts">
                      <InputNumber />
                    </Form.Item>
                    <Form.Item label={t('staffsPage_ContractType')} name="contractType">
                      <Input />
                    </Form.Item>
                    <Form.Item label={t('staffsPage_Seniority')} name="seniority">
                      <Input />
                    </Form.Item>

                    <Form.Item label={t('staffsPage_CnpsId')} name="cnpsId">
                      <Input />
                    </Form.Item>

                    <Form.Item label={t('staffsPage_Employment')} name="employment">
                      <Input />
                    </Form.Item>

                    <Form.Item label={t('staffsPage_SalaryCategory')} name="salaryCategory">
                      <Input />
                    </Form.Item>

                    <Form.Item label={t('staffsPage_Qualification')} name="qualification">
                      <Input />
                    </Form.Item>

                    <Form.Item label={t('staffsPage_EmployeeType')} name="employeeType">
                      <Select options={EmployeeType.__getSelectOptions()} className="input-value-color" disabled={!isNewForm} />
                    </Form.Item>

                    <Form.Item label="  " colon={false} name="canLogin" valuePropName="checked">
                      <Checkbox>{t('staffsPage_CanLogin')}</Checkbox>
                    </Form.Item>
                    <Form.Item name="imgUrl" label={t('staffsPage_StaffImage')}>
                      <SingleImageUpload blobStorageContainer={BlobStorageContainerType.EmployeesContainer} />
                    </Form.Item>
                  </Col>
                  <Col md={8}>
                    <Card size="small" className="mtopo-card" title={t('staffsPage_Roles')}>
                      <Form.Item name="roles">
                        <Checkbox.Group className="checkbox-option-single-line" options={roles} />
                      </Form.Item>
                    </Card>
                    {selectedRoles.includes(CHECK_IN_OPERATOR) ? (
                      <Card size="small" className="mtopo-card" title={t('staffsPage_Stations')}>
                        <Form.Item name="stationIds" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                          <StationInput mode="multiple" />
                        </Form.Item>
                      </Card>
                    ) : null}
                    {!isNewForm ? (
                      <Card size="small" className="mtopo-card" title={t('staffsPage_OtherLinks')}>
                        {otherLinks.length === 0
                          ? t('staffsPage_OtherLinksNone')
                          : otherLinks.map((x, index) => (
                              <div key={index}>
                                {x.stationName} :{' '}
                                {x.registerName
                                  ? t('staffsPage_Link_Register', { register: x.registerName })
                                  : t(`staffsPage_Link_${x.roleName}`, { defaultValue: x.roleName })}
                              </div>
                            ))}
                      </Card>
                    ) : null}
                    {employeeType === EmployeeType.Employee ? (
                      <Card size="small" className="mtopo-card" title={t('staffsPage_PaymentMethod')}>
                        {/* cash */}
                        <Form.Item name="isCashPaymentSelected" valuePropName="checked">
                          <Checkbox>{t('staffsPage_Cash')}</Checkbox>
                        </Form.Item>
                        <Form.Item
                          labelCol={{ md: 12 }}
                          label={t('staffsPage_CashPaymentPortion')}
                          name="cashPortion"
                          rules={[{ required: isCashPaymentSelected, message: VALIDATE_REQUIRED }]}
                        >
                          <InputNumber
                            placeholder={t('staffsPage_CashPaymentPortion')}
                            min={0}
                            max={100}
                            addonAfter="(%)"
                            className="w-100"
                            disabled={!isCashPaymentSelected}
                          />
                        </Form.Item>
                        <Divider />
                        {/* mobile money account number */}
                        <Form.Item name="isMobilePaymentSelected" valuePropName="checked">
                          <Checkbox>{t('staffsPage_MMAccountNumber')}</Checkbox>
                        </Form.Item>
                        <Form.Item
                          labelCol={{ md: 12 }}
                          label={t('staffsPage_AccountNumber')}
                          name="mobileAccountNumber"
                          rules={[{ required: isMobilePaymentSelected, message: VALIDATE_REQUIRED }]}
                        >
                          <Input placeholder={t('staffsPage_AccountNumber')} className="w-100" disabled={!isMobilePaymentSelected} />
                        </Form.Item>
                        <Form.Item
                          labelCol={{ md: 12 }}
                          label={t('staffsPage_MobilePaymentPortion')}
                          name="mobileAccountPortion"
                          rules={[{ required: isMobilePaymentSelected, message: VALIDATE_REQUIRED }]}
                        >
                          <InputNumber
                            placeholder={t('staffsPage_MobilePaymentPortion')}
                            min={0}
                            max={100}
                            className="w-100"
                            addonAfter="(%)"
                            disabled={!isMobilePaymentSelected}
                          />
                        </Form.Item>
                        <Divider />
                        {/* bank account number */}
                        <Form.Item name="isBankPaymentSelected" valuePropName="checked">
                          <Checkbox>{t('staffsPage_BankAccountNumber')}</Checkbox>
                        </Form.Item>
                        <Form.Item
                          labelCol={{ md: 12 }}
                          label={t('staffsPage_AccountNumber')}
                          name="bankAccountNumber"
                          rules={[{ required: isBankPaymentSelected, message: VALIDATE_REQUIRED }]}
                        >
                          <Input placeholder={t('staffsPage_AccountNumber')} min={0} className="w-100" disabled={!isBankPaymentSelected} />
                        </Form.Item>
                        <Form.Item
                          labelCol={{ md: 12 }}
                          label={t('staffsPage_BankAccountPortion')}
                          name="bankAccountPortion"
                          rules={[{ required: isBankPaymentSelected, message: VALIDATE_REQUIRED }]}
                        >
                          <InputNumber
                            placeholder={t('staffsPage_BankAccountPortion')}
                            min={0}
                            max={100}
                            addonAfter="(%)"
                            className="w-100"
                            disabled={!isBankPaymentSelected}
                          />
                        </Form.Item>
                      </Card>
                    ) : null}
                  </Col>
                  <Col md={8}>
                    <Card size="small" className="mtopo-card" title={t('companyRolesPage_Permissions')}>
                      <Form.Item name="permissions">
                        <Checkbox.Group className="checkbox-option-single-line" options={permissions} />
                      </Form.Item>
                    </Card>
                  </Col>
                </Row>
              </Form>
            </Spin>
          </Modal>
        ) : null}
      </div>
      {showExportModal ? (
        <ExportColumnsSelectorModal
          title={t('staffsPage_ExportStaffs')}
          onClose={() => toggleExportModal(false)}
          columns={exportColumns}
          onExportExcel={onExportExcel}
          onExportPdf={onExportPdf}
        />
      ) : null}
    </MainContent>
  );
};
export default AccountTab;
